import axios, { AxiosError, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import { dispatch } from 'store';
import accountReducer from 'store/accountReducer';
import { LOGOUT } from 'store/actions';
import { openSnackbar } from 'store/slices/snackbar';

export const axiosServices = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL
});

export const refreshApi = axios.create({
  baseURL: process.env.NEXT_PUBLIC_API_BASE_URL
});

interface RefreshResponse {
  accessToken: { token: string; expiration: string };
  refreshToken: string;
}

interface RetryableAxiosRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

interface ErrorDetail {
  detail: string;
  instance: string;
  status: number;
  title: string;
  type: string;
}

let isRefreshing = false;
let refreshSubscribers: ((token: string) => void)[] = [];

const refreshToken = async (): Promise<string> => {
  try {
    const token = localStorage.getItem('token');
    const refreshToken = localStorage.getItem('refreshToken');

    refreshApi.defaults.headers.common.Authorization = `Bearer ${token}`;

    const response = await refreshApi.post<RefreshResponse>('/api/auth/refresh', { refreshToken });

    localStorage.setItem('token', response.data.accessToken.token);
    localStorage.setItem('refreshToken', response.data.refreshToken);
    return response.data.accessToken.token;
  } catch (error) {
    throw error;
  }
};

const onRefreshed = (newToken: string): void => {
  isRefreshing = false;
  refreshSubscribers.forEach((callback) => callback(newToken));
  refreshSubscribers = [];
};

const logout = () => {
  accountReducer(undefined, {
    type: LOGOUT
  });
  localStorage.removeItem('token');
  localStorage.removeItem('refreshToken');
  window.location.replace('/login');
};

axiosServices.interceptors.request.use(
  async (config: RetryableAxiosRequestConfig): Promise<RetryableAxiosRequestConfig> => {
    const inkaConfigString = window.localStorage.getItem('inka-config-ts');
    let inkaConfig: { [key: string]: any } | null = null;

    if (inkaConfigString !== null) {
      inkaConfig = JSON.parse(inkaConfigString);
    }

    if (inkaConfig && inkaConfig.locale) {
      config.headers['x-lang'] = inkaConfig.locale === 'en-US' ? 'en-US' : 'tr-TR';
    } else {
      config.headers['x-lang'] = 'tr-TR';
    }

    return config;
  },
  (error: AxiosError) => Promise.reject(error)
);

axiosServices.interceptors.response.use(
  (response: AxiosResponse) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as RetryableAxiosRequestConfig;
    const errorDetail = error.response?.data as ErrorDetail;

    if ((error.response?.status === 401 || error.response?.status === 403) && !originalRequest._retry) {
      switch (errorDetail?.detail) {
        case 'Token expired':
        case 'Oturum süresi doldu':
          if (!isRefreshing) {
            isRefreshing = true;
            try {
              const newToken = await refreshToken();
              onRefreshed(newToken);
              axiosServices.defaults.headers.common.Authorization = `Bearer ${newToken}`;
              originalRequest.headers['Authorization'] = `Bearer ${newToken}`;
              originalRequest._retry = true;
              return axiosServices(originalRequest);
            } catch (refreshError) {
              await logout();
              return Promise.reject(refreshError);
            }
          }
          break;
        case 'You are not authenticated':
        case 'Kimliğiniz doğrulanamadı':
        case 'Signature key failed':
        case 'Şifreleme anahtar hatası':
          await logout();
          dispatch(
            openSnackbar({
              message: errorDetail.detail,
              variant: 'alert',
              alert: {
                color: 'error',
                severity: 'error'
              }
            })
          );
          return Promise.reject(error);
        case 'You are not authorized':
        case 'Yetkisiz işlem':
          window.location.replace('/403');
          dispatch(
            openSnackbar({
              message: errorDetail.detail,
              variant: 'alert',
              alert: {
                color: 'error',
                severity: 'error'
              }
            })
          );
          return Promise.reject(error);
        default:
          return Promise.reject(error);
      }
    } else {
      dispatch(
        openSnackbar({
          message: errorDetail.detail,
          variant: 'alert',
          alert: {
            color: 'error',
            severity: 'error'
          }
        })
      );
    }

    return Promise.reject(error);
  }
);

export default axiosServices;
